import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { MarkEmailRead as MailIcon } from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const GooglePostmasterSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [brands, setBrands] = useState([]);
  const [domainBrandMapping, setDomainBrandMapping] = useState({});

  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');
  const brandId = urlParams.get('brand_id');

  useEffect(() => {
    if (!tempId) {
      setError('Missing temporary ID. Please restart the OAuth process.');
      setLoading(false);
      return;
    }
    fetchDomains();
    fetchBrands();
  }, [tempId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await api.get('/api/connectors/google/postmaster/domains', {
        params: { temp_id: tempId }
      });
      if (response.data.success) {
        setDomains(response.data.domains);
      } else {
        setError(response.data.message || 'Failed to fetch domains');
      }
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError(err.response?.data?.message || 'Failed to fetch Postmaster domains');
    } finally {
      setLoading(false);
    }
  };

  const handleDomainSelection = (domain, checked) => {
    if (checked) {
      const d = domains.find(x => x.domain === domain);
      setSelectedDomains([...selectedDomains, d]);
      // For already-connected domains, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth URL params.
      const defaultBrand = d?.alreadyConnected && d?.connectedBrandId
        ? String(d.connectedBrandId)
        : brandId;
      setDomainBrandMapping(prev => ({ ...prev, [domain]: defaultBrand }));
    } else {
      setSelectedDomains(selectedDomains.filter(x => x.domain !== domain));
      setDomainBrandMapping(prev => {
        const next = { ...prev };
        delete next[domain];
        return next;
      });
    }
  };

  const handleBrandChange = (domain, newBrandId) => {
    setDomainBrandMapping(prev => ({ ...prev, [domain]: newBrandId }));
  };

  const handleCreateConnectors = async () => {
    if (selectedDomains.length === 0) {
      setError('Please select at least one domain to connect');
      return;
    }
    const withoutBrands = selectedDomains.filter(d => !domainBrandMapping[d.domain]);
    if (withoutBrands.length > 0) {
      setError('Please select a brand for all selected domains');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const createdConnectors = [];
      for (const d of selectedDomains) {
        const selectedBrandId = domainBrandMapping[d.domain] || brandId;
        const response = await api.post('/api/connectors/google/postmaster/create', {
          temp_id: tempId,
          brand_id: parseInt(selectedBrandId),
          domain: d.domain,
          domain_permission: d.permission
        });
        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${d.domain}: ${response.data.message}`);
        }
      }
      const uniqueBrands = [...new Set(selectedDomains.map(d =>
        getBrandName(domainBrandMapping[d.domain] || brandId)
      ))];
      navigate('/connectors', {
        state: {
          message: `Successfully created ${createdConnectors.length} Google Postmaster connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          type: 'success'
        }
      });
    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (bId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(bId));
    return brand ? brand.name : 'Unknown Brand';
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'OWNER': return 'success';
      case 'READER': return 'primary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Postmaster domains...</Typography>
      </Box>
    );
  }

  if (error && domains.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>Back to Connectors</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <MailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>Select Google Postmaster Domains</Typography>
        <Typography variant="body1" color="text.secondary">
          Choose one or more domains to monitor. You can assign each domain to a different brand.
          Domains where you are not an owner or reader cannot be connected.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {domains.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Postmaster domains found. Register and verify your sending domains in Google Postmaster Tools first.
        </Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Available Domains ({domains.length})
          </Typography>

          <Grid container spacing={2}>
            {domains.map((d) => {
              const disabled = creating || d.permission === 'NONE';
              return (
                <Grid item xs={12} md={6} key={d.domain}>
                  <Card sx={{ height: '100%', opacity: d.permission === 'NONE' ? 0.6 : 1 }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDomains.some(x => x.domain === d.domain)}
                            onChange={(e) => handleDomainSelection(d.domain, e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label={
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                              {d.domain}
                            </Typography>
                            {d.alreadyConnected && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={d.connectedBrandName
                                    ? `Connected · ${d.connectedBrandName}`
                                    : 'Already connected'}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Box>
                            )}
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                              <Chip label={d.permission} size="small" color={getPermissionColor(d.permission)} />
                              <Chip label={d.verificationState} size="small" variant="outlined" />
                            </Box>

                            {selectedDomains.some(x => x.domain === d.domain) && (
                              <Box sx={{ mt: 2 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`brand-select-${d.domain}`}>Assign to Brand</InputLabel>
                                  <Select
                                    labelId={`brand-select-${d.domain}`}
                                    value={domainBrandMapping[d.domain] || ''}
                                    label="Assign to Brand"
                                    onChange={(e) => handleBrandChange(d.domain, e.target.value)}
                                    disabled={creating}
                                    sx={{ borderRadius: 1 }}
                                  >
                                    {brands.map(brand => (
                                      <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {selectedDomains.length} domain(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={creating}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCreateConnectors}
                disabled={selectedDomains.length === 0 || creating}
                startIcon={creating ? <CircularProgress size={20} /> : null}
              >
                {creating ? 'Creating...' : `Create ${selectedDomains.length} Connector(s)`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default GooglePostmasterSelection;
